import React, { useState, useEffect, useReducer } from 'react';
import { useAuth } from '../contexts/AuthContext';
import VNCConsole from './VNCConsole';
import ChangePassword from './ChangePassword';
import {
    LogOut,
    Server,
    Play,
    Square,
    RotateCcw,
    Monitor,
    Settings,
    Activity,
    HardDrive,
    Cpu,
    MemoryStick,
    Plus,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Terminal,
    Archive,
    RefreshCw,
    Calendar,
    Trash2,
    Download,
    Camera,
    Network,
    ChevronDown,
    ChevronRight,
    HelpCircle
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import axios from 'axios';

// Registrar componentes do Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

// Auto-refresh intervals (in milliseconds)
const VM_REFRESH_INTERVAL = 5000; // 5 seconds for VM status
const LOGS_REFRESH_INTERVAL = 60000; // 1 minute for logs
const REQUESTS_REFRESH_INTERVAL = 30000; // 30 seconds for VM requests

var selectedVM = null;

function Dashboard() {
    const { user, logout } = useAuth();
    const [vms, setVms] = useState([]);
    const [logs, setLogs] = useState([]);
    const [vmRequests, setVmRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState({});
    const [showVMRequestModal, setShowVMRequestModal] = useState(false);
    const [vmModal, setVMModal] = useState(null);
    const [showVMDetailsModal, setShowVMDetailsModal] = useState(false);
    const [showConsoleModal, setShowConsoleModal] = useState(false);
    const [autoRefreshing, setAutoRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [isVMRequestsExpanded, setIsVMRequestsExpanded] = useState(false);

    useEffect(() => {
        // Verificar se o usuário precisa alterar a senha
        if (user && user.must_change_password) {
            setShowChangePasswordModal(true);
        }

        if (user.role === 'admin')
            setIsVMRequestsExpanded(true);

        fetchVMs();
        fetchLogs();
        fetchVMRequests();

        // Set up auto-refresh intervals
        const vmInterval = setInterval(() => fetchVMs(true), VM_REFRESH_INTERVAL);
        const logsInterval = setInterval(() => fetchLogs(true), LOGS_REFRESH_INTERVAL);
        const requestsInterval = setInterval(() => fetchVMRequests(true), REQUESTS_REFRESH_INTERVAL);

        // Cleanup intervals on component unmount
        return () => {
            clearInterval(vmInterval);
            clearInterval(logsInterval);
            clearInterval(requestsInterval);
        };
    }, [user]);

    const fetchVMs = async (isAutoRefresh = false) => {
        if (isAutoRefresh) {
            setAutoRefreshing(true);
        }

        try {
            const response = await axios.get('/api/client/vms');
            setVms(response.data);

            // Update selectedVM if modal is open and VM exists in the new data
            if (selectedVM) {
                const updatedVM = response.data.find(vm =>
                    vm.nodeId === selectedVM.nodeId && vm.vmid === selectedVM.vmid
                );
                if (updatedVM) {
                    selectedVM = updatedVM;
                    setVMModal(updatedVM);
                    // document.dispatchEvent(new CustomEvent('vmUpdated', {detail:{
                    //     vm:updatedVM
                    // }}))
                }
            }
        } catch (error) {
            console.error('Erro ao buscar VMs:', error);
            if (error.response?.status === 401) {
                logout();
            }
        } finally {
            setLoading(false);
            if (isAutoRefresh) {
                setAutoRefreshing(false);
                setLastRefresh(new Date());
            }
        }
    };

    const fetchLogs = async (isAutoRefresh = false) => {
        try {
            const response = await axios.get('/api/logs');
            setLogs(response.data);
        } catch (error) {
            console.error('Erro ao buscar logs:', error);
        } finally {
            if (isAutoRefresh) {
                setLastRefresh(new Date());
            }
        }
    };

    const fetchVMRequests = async (isAutoRefresh = false) => {
        try {
            const response = await axios.get('/api/vm-requests');
            setVmRequests(response.data);
        } catch (error) {
            console.error('Erro ao buscar solicitações de VM:', error);
        } finally {
            if (isAutoRefresh) {
                setLastRefresh(new Date());
            }
        }
    };

    const getRequestStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'text-yellow-600 bg-yellow-100';
            case 'approved': return 'text-green-600 bg-green-100';
            case 'rejected': return 'text-red-600 bg-red-100';
            case 'created': return 'text-blue-600 bg-blue-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getRequestStatusText = (status) => {
        switch (status) {
            case 'pending': return 'Pendente';
            case 'approved': return 'Aprovada';
            case 'rejected': return 'Rejeitada';
            case 'created': return 'Criada';
            default: return 'Desconhecido';
        }
    };

    const getRequestStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <Clock className="h-4 w-4" />;
            case 'approved': return <CheckCircle className="h-4 w-4" />;
            case 'rejected': return <XCircle className="h-4 w-4" />;
            case 'created': return <Server className="h-4 w-4" />;
            default: return <AlertCircle className="h-4 w-4" />;
        }
    };

    const handleVMAction = async (nodeId, vmtype, vmid, action, force = false) => {
        const actionKey = `${nodeId}-${vmid}-${action}`;
        setActionLoading(prev => ({ ...prev, [actionKey]: true }));

        try {
            const endpoint = `/api/vm/${nodeId}/${vmtype}/${vmid}/${action}`;
            const data = action === 'stop' ? { force } : {};
            // console.log(`ACAO=${endpoint} D=${data}`)

            const resultado = await axios.post(endpoint, data);
            // console.debug(`RESULT ACTION`,resultado.data.data)

            // Refresh VMs and logs
            await fetchVMs();
            await fetchLogs();
        } catch (error) {
            console.error(`Erro ao ${action} VM:`, error);
            alert(`Erro ao ${action} VM: ${error.response?.data?.error || error.message}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [actionKey]: false }));
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'running': return 'text-green-600 bg-green-100';
            case 'stopped': return 'text-red-600 bg-red-100';
            case 'paused': return 'text-yellow-600 bg-yellow-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusText = (status, lock) => {
        if(lock) return 'Trancada';
        switch (status) {
            case 'running': return 'Ligada';
            case 'stopped': return 'Desligada';
            case 'paused': return 'Pausada';
            default: return 'Desconhecido';
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatUptime = (seconds) => {
        if (!seconds) return 'N/A';
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (days > 0) {
            return `${days}d ${hours}h ${minutes}m`;
        } else if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else {
            return `${minutes}m`;
        }
    };

    const handleVMClick = (vm) => {
        selectedVM = vm;
        setVMModal(vm);
        setShowVMDetailsModal(true);
    };

    const handleConsoleAccess = (vm) => {
        selectedVM = vm;
        setVMModal(vm);
        setShowConsoleModal(true);
        console.log(`CLICK2_CONSOLE`, selectedVM)
    };

    const handlePasswordChanged = () => {
        setShowChangePasswordModal(false);
        // O contexto já foi atualizado no componente ChangePassword
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4 sm:py-6">
                        <div className="flex items-center">
                            {/* <Server className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mr-2 sm:mr-3" /> */}
                            <div class="w-32 mx-auto sm:w-40 rounded-full">
                                <img src="https://repo.telic.net.br/imgdir/Tglobal_360x100px_2.png" />
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            <span className="text-xs sm:text-sm text-gray-700 hidden sm:block">Olá, {user.full_name || user.email}</span>
                            {user.role === 'admin' && (
                                <a
                                    href="/admin"
                                    className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 border border-transparent text-xs sm:text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <Settings className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                                    <span className="hidden sm:inline">Admin</span>
                                </a>
                            )}
                            <button
                                onClick={logout}
                                className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 border border-transparent text-xs sm:text-sm leading-4 font-medium rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300"
                            >
                                <LogOut className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                                <span className="hidden sm:inline">Sair</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                {/* Auto-refresh indicator */}
                <div className="px-4 sm:px-0 mb-4">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center space-x-2">
                            {autoRefreshing && (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500 mr-1"></div>
                                    <span>Atualizando...</span>
                                </div>
                            )}
                            {!autoRefreshing && (
                                <div className="flex items-center">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                                    <span>Atualização automática ativa</span>
                                </div>
                            )}
                        </div>
                        <div>
                            Última atualização: {lastRefresh.toLocaleTimeString('pt-BR')}
                        </div>
                    </div>
                </div>

                {/* VM Requests Section */}
                <div className="px-4 py-6 sm:px-0">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
                        <button
                            onClick={() => setIsVMRequestsExpanded(!isVMRequestsExpanded)}
                            className="flex items-center text-xl sm:text-2xl font-bold text-gray-900 hover:text-gray-700 transition-colors"
                        >
                            {isVMRequestsExpanded ? (
                                <ChevronDown className="h-6 w-6 mr-2" />
                            ) : (
                                <ChevronRight className="h-6 w-6 mr-2" />
                            )}
                            Suas Solicitações de VM
                            {vmRequests.length > 0 && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {vmRequests.length}
                                </span>
                            )}
                        </button>
                        {(user.role !== 'admin') &&
                            <button
                                onClick={() => setShowVMRequestModal(true)}
                                className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Solicitar Nova VM
                            </button>}
                    </div>

                    {isVMRequestsExpanded && (
                        <>
                            {vmRequests.length === 0 ? (
                                <div className="text-center py-8 bg-white rounded-lg shadow">
                                    <Clock className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma solicitação encontrada</h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Clique em "Solicitar Nova VM" para criar sua primeira solicitação.
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                                    <ul className="divide-y divide-gray-200">
                                        {vmRequests.map((request) => (
                                            <li key={request.id} className="px-6 py-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center">
                                                        {getRequestStatusIcon(request.status)}
                                                        <div className="ml-4">
                                                            <h3 className="text-lg font-medium text-gray-900">{request.vm_name}</h3>
                                                            <p className="text-sm text-gray-500">
                                                                {request.cpu_cores} CPU • {request.memory_gb}GB RAM • {request.disk_gb}GB Disco
                                                            </p>
                                                            <p className="text-sm text-gray-500">
                                                                Solicitado em: {new Date(request.created_at).toLocaleString('pt-BR')}
                                                            </p>
                                                            {request.description && (
                                                                <p className="text-sm text-gray-600 mt-1">{request.description}</p>
                                                            )}
                                                            {request.admin_notes && (
                                                                <p className="text-sm text-blue-600 mt-1">
                                                                    <strong>Observações do Admin:</strong> {request.admin_notes}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRequestStatusColor(request.status)}`}>
                                                            {getRequestStatusText(request.status)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* VMs Grid */}
                {user.role !== 'admin' &&
                    <div className="px-4 py-6 sm:px-0">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Suas Máquinas Virtuais</h2>

                        {vms.length === 0 ? (
                            <div className="text-center py-12">
                                <Server className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma VM encontrada</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Você não possui VMs atribuídas ou elas não estão disponíveis.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                {vms.map((vm) => (
                                    <div key={`${vm.nodeId}-${vm.vmid}`} className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleVMClick(vm)}>
                                        <div className="p-4 sm:p-6">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <Monitor className="h-8 w-8 text-gray-400" />
                                                    <div className="ml-3">
                                                        <h3 className="text-lg font-medium text-gray-900">{vm.name}</h3>
                                                        <p className="text-sm text-gray-500">ID: {vm.vmid}</p>
                                                    </div>
                                                </div>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vm.status)}`}>
                                                    {getStatusText(vm.status, vm['lock'])}
                                                </span>
                                            </div>

                                            <div className="mt-4 space-y-2">
                                                <div className="flex items-center text-sm text-gray-600">
                                                    <Cpu className="h-4 w-4 mr-2" />
                                                    <span>CPU: {vm.cpu ? `${(vm.cpu * 100).toFixed(1)}%` : '0.0%'} ({vm.maxcpu || 'N/A'} cores)</span>
                                                </div>
                                                <div className="flex items-center text-sm text-gray-600">
                                                    <MemoryStick className="h-4 w-4 mr-2" />
                                                    <span>RAM: {formatBytes(vm.mem)} / {formatBytes(vm.maxmem)}</span>
                                                </div>
                                                <div className="flex items-center text-sm text-gray-600">
                                                    <HardDrive className="h-4 w-4 mr-2" />
                                                    <span>Disco: {formatBytes(vm.disk)} / {formatBytes(vm.maxdisk)}</span>
                                                </div>
                                                <div className="flex items-center text-sm text-gray-600">
                                                    <Server className="h-4 w-4 mr-2" />
                                                    <span>Região: {vm.clusterName || vm.nodeName}</span>
                                                </div>

                                                {vm.uptime ? (
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <Clock className="h-4 w-4 mr-2" />
                                                        <span>Uptime: {formatUptime(vm.uptime)}</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <br />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mt-6 space-y-2" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex space-x-2">
                                                    {vm.status === 'stopped' ? (
                                                        <button
                                                            onClick={() => handleVMAction(vm.nodeId, vm.type, vm.vmid, 'start')}
                                                            disabled={actionLoading[`${vm.nodeId}-${vm.vmid}-start`]}
                                                            className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                                        >
                                                            {actionLoading[`${vm.nodeId}-${vm.vmid}-start`] ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                            ) : (
                                                                <>
                                                                    <Play className="h-4 w-4 mr-1" />
                                                                    Ligar
                                                                </>
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => handleVMAction(vm.nodeId, vm.type, vm.vmid, 'stop')}
                                                                disabled={actionLoading[`${vm.nodeId}-${vm.vmid}-stop`]}
                                                                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                                            >
                                                                {actionLoading[`${vm.nodeId}-${vm.vmid}-stop`] ? (
                                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                                ) : (
                                                                    <>
                                                                        <Square className="h-4 w-4 mr-1" />
                                                                        Desligar
                                                                    </>
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => handleVMAction(vm.nodeId, vm.type, vm.vmid, 'reboot')}
                                                                disabled={actionLoading[`${vm.nodeId}-${vm.vmid}-reboot`]}
                                                                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                                            >
                                                                {actionLoading[`${vm.nodeId}-${vm.vmid}-reboot`] ? (
                                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                                                                ) : (
                                                                    <>
                                                                        <RotateCcw className="h-4 w-4 mr-1" />
                                                                        Reiniciar
                                                                    </>
                                                                )}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => handleConsoleAccess(vm)}
                                                    className="w-full inline-flex justify-center items-center px-3 py-2 border border-blue-300 text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                                                >
                                                    <Terminal className="h-4 w-4 mr-1" />
                                                    Console
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>}



                {/* Activity Logs */}
                <div className="px-4 py-6 sm:px-0">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                        <Activity className="h-6 w-6 mr-2" />
                        Histórico de Ações
                    </h2>

                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                        <ul className="divide-y divide-gray-200">
                            {logs.slice(0, 10).map((log) => (
                                <li key={log.id} className="px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${log.status === 'success' ? 'bg-green-400' : 'bg-red-400'}`}></div>
                                            <div className="ml-4">
                                                <p className="text-sm font-medium text-gray-900">
                                                    {log.action} - {log.vm_name || `VM ${log.vmid}`}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {new Date(log.created_at).toLocaleString('pt-BR')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                {log.status === 'success' ? 'Sucesso' : 'Erro'}
                                            </span>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </main>

            {/* VM Request Modal */}
            {showVMRequestModal && (
                <VMRequestModal
                    onClose={() => setShowVMRequestModal(false)}
                    onSave={() => {
                        setShowVMRequestModal(false);
                        fetchVMRequests();
                    }}
                />
            )}

            {/* VM Details Modal */}
            {showVMDetailsModal && selectedVM && (
                <VMDetailsModal
                    vm={vmModal}
                    onClose={() => {
                        setShowVMDetailsModal(false);
                        selectedVM = null;
                    }}
                    onVMAction={handleVMAction}
                    onConsoleAccess={handleConsoleAccess}
                    actionLoading={actionLoading}
                    formatBytes={formatBytes}
                    formatUptime={formatUptime}
                    getStatusColor={getStatusColor}
                    getStatusText={getStatusText}
                />
            )}

            {/* VNC Console */}
            {showConsoleModal && selectedVM && (
                <VNCConsole
                    nodeId={vmModal.nodeId}
                    vmid={vmModal.vmid}
                    vmName={vmModal.name}
                    vmType={vmModal.type}
                    node={vmModal.node}
                    onClose={() => {
                        setShowConsoleModal(false);
                        selectedVM = null;
                    }}
                />
            )}

            {/* Change Password Modal */}
            {showChangePasswordModal && (
                <ChangePassword
                    onPasswordChanged={handlePasswordChanged}
                />
            )}

            {/* Support FAB - Only for clients */}
            {user.role !== 'admin' && (
                <button
                    onClick={() => window.open('https://suporte.telic.com.br', '_blank')}
                    className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 z-50 group"
                    title="Suporte"
                >
                    <HelpCircle className="h-6 w-6" />
                    <span className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                        Suporte
                    </span>
                </button>
            )}
        </div>
    );
}

// VM Request Modal Component
function VMRequestModal({ onClose, onSave }) {
    const [formData, setFormData] = useState({
        vm_name: '',
        cpu_cores: 1,
        memory_gb: 1,
        storages: [{ size_gb: 20 }],
        region_node_id: '',
        os_template: '',
        custom_os_url: '',
        description: ''
    });
    const [loading, setLoading] = useState(false);
    const [availableOS, setAvailableOS] = useState([]);
    const [regions, setRegions] = useState([]);
    const [pricing, setPricing] = useState({
        cpu_price_per_core: 0,
        ram_price_per_gb: 0,
        storage_price_per_gb: 0,
        base_price: 0
    });
    const [estimatedPrice, setEstimatedPrice] = useState(0);

    useEffect(() => {
        fetchConfigData();
    }, []);

    useEffect(() => {
        calculatePrice();
    }, [formData, pricing]);

    const fetchConfigData = async () => {
        try {
            const [osResponse, regionsResponse, pricingResponse] = await Promise.all([
                axios.get('/api/vm-request-config/os'),
                axios.get('/api/vm-request-config/clusters'),
                axios.get('/api/vm-request-config/pricing')
            ]);

            setAvailableOS(osResponse.data);
            setRegions(regionsResponse.data);
            setPricing(pricingResponse.data);
        } catch (error) {
            console.error('Erro ao carregar configurações:', error);
        }
    };

    const calculatePrice = () => {
        const cpuCost = formData.cpu_cores * pricing.cpu_price_per_core;
        const ramCost = formData.memory_gb * pricing.ram_price_per_gb;
        const storageCost = formData.storages.reduce((total, storage) =>
            total + (storage.size_gb * pricing.storage_price_per_gb), 0);

        const total = parseFloat(pricing.base_price) + cpuCost + ramCost + storageCost;
        setEstimatedPrice(total);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const submitData = {
                ...formData,
                estimated_price: estimatedPrice
            };
            await axios.post('/api/vm-requests', submitData);
            onSave();
        } catch (error) {
            console.error('Erro ao criar solicitação:', error);
            alert(`Erro ao criar solicitação: ${error.response?.data?.error || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'number' ? parseInt(value) || 0 : value
        });
    };

    const handleSliderChange = (name, value) => {
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleStorageChange = (index, value) => {
        const newStorages = [...formData.storages];
        newStorages[index].size_gb = value;
        setFormData({
            ...formData,
            storages: newStorages
        });
    };

    const addStorage = () => {
        setFormData({
            ...formData,
            storages: [...formData.storages, { size_gb: 20 }]
        });
    };

    const removeStorage = (index) => {
        if (formData.storages.length > 1) {
            const newStorages = formData.storages.filter((_, i) => i !== index);
            setFormData({
                ...formData,
                storages: newStorages
            });
        }
    };

    const isCustomOS = formData.os_template === 'custom';

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-6 border w-full max-w-6xl shadow-lg rounded-md bg-white m-4">
                <div className="mt-3">
                    <h3 className="text-xl font-medium text-gray-900 mb-6">
                        Solicitar Nova VM
                    </h3>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Nome da VM *</label>
                                <input
                                    type="text"
                                    name="vm_name"
                                    value={formData.vm_name}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Ex: minha-vm-web"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Região *</label>
                                <select
                                    name="region_node_id"
                                    value={formData.region_node_id}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Selecione uma região</option>
                                    {regions.map(region => (
                                        <option key={region.id} value={region.id}>
                                            {region.display_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* CPU and RAM Sliders */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    CPU Cores: {formData.cpu_cores} {formData.cpu_cores === 1 ? 'core' : 'cores'}
                                </label>
                                <input
                                    type="range"
                                    min="1"
                                    max="16"
                                    step="1"
                                    value={formData.cpu_cores}
                                    onChange={(e) => handleSliderChange('cpu_cores', parseInt(e.target.value))}
                                    className="w-full rounded-lg cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>1</span>
                                    <span>16</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Memória RAM: {formData.memory_gb} GB
                                </label>
                                <input
                                    type="range"
                                    min="1"
                                    max="64"
                                    step="1"
                                    value={formData.memory_gb}
                                    onChange={(e) => handleSliderChange('memory_gb', parseInt(e.target.value))}
                                    className="w-full rounded-lg cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>1 GB</span>
                                    <span>64 GB</span>
                                </div>
                            </div>
                        </div>

                        {/* Storage Units */}
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <label className="block text-sm font-medium text-gray-700">
                                    Unidades de Armazenamento
                                </label>
                                <button
                                    type="button"
                                    onClick={addStorage}
                                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                                >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Adicionar
                                </button>
                            </div>

                            {formData.storages.map((storage, index) => (
                                <div key={index} className="mb-4 p-3 border border-gray-200 rounded-md">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-medium text-gray-700">
                                            Disco {index + 1}: {storage.size_gb} GB
                                        </span>
                                        {formData.storages.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeStorage(index)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                    <input
                                        type="range"
                                        min="10"
                                        max="1000"
                                        step="10"
                                        value={storage.size_gb}
                                        onChange={(e) => handleStorageChange(index, parseInt(e.target.value))}
                                        className="w-full rounded-lg cursor-pointer"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>10 GB</span>
                                        <span>1 TB</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Operating System and Description */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Sistema Operacional</label>
                                <select
                                    name="os_template"
                                    value={formData.os_template}
                                    onChange={handleChange}
                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Selecione um SO</option>
                                    {availableOS.map(os => (
                                        <option key={os.id} value={os.name}>
                                            {os.display_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Descrição/Justificativa</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={3}
                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Descreva o uso pretendido para esta VM..."
                                />
                            </div>
                        </div>

                        {/* Custom OS URL */}
                        {isCustomOS && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">URL da ISO Personalizada *</label>
                                <input
                                    type="url"
                                    name="custom_os_url"
                                    value={formData.custom_os_url}
                                    onChange={handleChange}
                                    required={isCustomOS}
                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="https://exemplo.com/minha-iso.iso"
                                />
                            </div>
                        )}

                        {/* Price Estimate */}
                        <div className="bg-blue-50 p-4 rounded-md text-center">
                            <h4 className="text-lg font-medium text-blue-900 mb-2">Estimativa de Valor</h4>
                            <div className="text-2xl font-bold text-blue-900">
                                R$ {estimatedPrice.toFixed(2)}/mês
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                            >
                                {loading ? 'Enviando...' : 'Solicitar VM'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}


// VM Details Modal Component
function VMDetailsModal({ vm, onClose, onVMAction, onConsoleAccess, actionLoading, formatBytes, formatUptime, getStatusColor, getStatusText }) {
    const [activeTabModalVM, setActiveTabModalVM] = useState('details');
    const [backups, setBackups] = useState([]);
    const [backupsLoading, setBackupsLoading] = useState(false);
    const [backupLoading, setBackupLoading] = useState(false);
    const [restoreLoading, setRestoreLoading] = useState({});
    const [deleteLoading, setDeleteLoading] = useState({});
    const [snapshots, setSnapshots] = useState([]);
    const [snapshotsLoading, setSnapshotsLoading] = useState(false);
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [snapshotRestoreLoading, setSnapshotRestoreLoading] = useState({});
    const [snapshotDeleteLoading, setSnapshotDeleteLoading] = useState({});
    const [showCreateSnapshotModal, setShowCreateSnapshotModal] = useState(false);
    const [snapshotName, setSnapshotName] = useState('');
    const [snapshotDescription, setSnapshotDescription] = useState('');
    const [statistics, setStatistics] = useState([]);
    const [statisticsLoading, setStatisticsLoading] = useState(false);
    const [statisticsTimeRange, setStatisticsTimeRange] = useState('hour');
    const [vmNotes, setVmNotes] = useState('');
    const [notesLoading, setNotesLoading] = useState(false);
    const [notesSaving, setNotesSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showChangeResourcesModal, setShowChangeResourcesModal] = useState(false);

    // Load backups when backup tab is selected
    useEffect(() => {
        if (activeTabModalVM === 'backups') {
            loadBackups();
        }
    }, [activeTabModalVM, vm.nodeId, vm.vmid]);

    // Load snapshots when snapshots tab is selected
    useEffect(() => {
        if (activeTabModalVM === 'snapshots') {
            loadSnapshots();
        }
    }, [activeTabModalVM, vm.nodeId, vm.vmid]);

    // Load statistics when statistics tab is selected
    useEffect(() => {
        if (activeTabModalVM === 'statistics') {
            loadStatistics();
        }
    }, [activeTabModalVM, statisticsTimeRange, vm.nodeId, vm.vmid]);

    // Load notes when details tab is selected or modal opens
    useEffect(() => {
        if (activeTabModalVM === 'details') {
            loadVmNotes();
        }
    }, [activeTabModalVM, vm.nodeId, vm.vmid]);

    // Force component update when VM data changes (following React's single source of truth principle)
    // useEffect(() => {
    //     // This effect will run whenever the vm prop changes, ensuring the modal stays in sync
    //     // with the parent component's state updates
    // }, [vmUpdated]);

    // new CustomEvent('');

    // addEventListener()

    // const evento = (e) => {
    //     console.log(e);
    // };
    // document.removeEventListener('vmUpdated',evento);
    // document.addEventListener('vmUpdated',evento);



    const loadBackups = async () => {
        try {
            setBackupsLoading(true);
            setError(null);

            const response = await axios.get(`/api/vm/${vm.nodeId}/${vm.vmid}/backups`);
            setBackups(response.data.backups || []);
        } catch (error) {
            console.error('Error loading backups:', error);
            setError(error.response?.data?.message || 'Erro ao carregar backups');
        } finally {
            setBackupsLoading(false);
        }
    };

    const createBackup = async () => {
        try {
            setBackupLoading(true);
            setError(null);
            setSuccess(null);

            const response = await axios.post(`/api/vm/${vm.nodeId}/${vm.vmid}/backup`);
            const taskId = response.data.taskId;

            if (taskId) {
                setSuccess('Backup iniciado! Monitorando progresso...');

                // Monitor task status until completion
                await monitorTaskStatus(taskId);
            } else {
                setSuccess('Backup iniciado! Aguarde alguns minutos até a conclusão.');
                // Fallback: reload backups after delay if no taskId
                setTimeout(() => {
                    loadBackups();
                    setBackupLoading(false);
                }, 30000);
            }
        } catch (error) {
            console.error('Error creating backup:', error);
            setError(error.response?.data?.message || 'Erro ao criar backup');
            setBackupLoading(false);
        }
    };

    const monitorTaskStatus = async (taskId) => {
        const checkInterval = 3000; // Check every 3 seconds
        const maxAttempts = 600; // Maximum 30 minutes (600 * 3 seconds)
        let attempts = 0;

        const checkStatus = async () => {
            try {
                attempts++;
                const response = await axios.get(`/api/vm/${vm.nodeId}/task/${taskId}/status`);
                const taskStatus = response.data;

                if (taskStatus.status === 'stopped') {
                    // Task completed
                    if (taskStatus.exitstatus === 'OK') {
                        setSuccess('Backup concluído com sucesso!');
                        loadBackups(); // Reload backup list
                    } else {
                        setError(`Backup falhou: ${taskStatus.exitstatus || 'Erro desconhecido'}`);
                    }
                    setBackupLoading(false);
                } else if (taskStatus.status === 'running') {
                    // Task still running
                    setSuccess(`Backup em progresso... (${Math.floor(attempts * checkInterval / 1000)}s)`);

                    if (attempts < maxAttempts) {
                        setTimeout(checkStatus, checkInterval);
                    } else {
                        // Timeout reached
                        setError('Timeout: Backup demorou mais que o esperado. Verifique manualmente.');
                        setBackupLoading(false);
                    }
                } else {
                    // Unknown status
                    setError(`Status da task desconhecido: ${taskStatus.status}`);
                    setBackupLoading(false);
                }
            } catch (error) {
                console.error('Error checking task status:', error);

                if (attempts < maxAttempts) {
                    // Retry on error
                    setTimeout(checkStatus, checkInterval);
                } else {
                    setError('Erro ao monitorar o progresso do backup. Verifique manualmente.');
                    setBackupLoading(false);
                }
            }
        };

        // Start monitoring
        setTimeout(checkStatus, checkInterval);
    };

    const formatBackupDate = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleString('pt-BR');
    };

    const restoreBackup = async (backupFile) => {
        if (!window.confirm(`Tem certeza que deseja restaurar o backup ${backupFile}? Esta ação irá sobrescrever o estado atual da VM.`)) {
            return;
        }

        try {
            setRestoreLoading(prev => ({ ...prev, [backupFile]: true }));
            setError(null);
            setSuccess(null);

            const response = await axios.post(`/api/vm/${vm.nodeId}/${vm.type}/${vm.vmid}/restore`, {
                backup: backupFile
            });

            setSuccess(`Restauração do backup ${backupFile} iniciada com sucesso!`);
        } catch (error) {
            console.error('Error restoring backup:', error);
            setError(error.response?.data?.error || error.response?.data?.message || 'Erro ao restaurar backup');
        } finally {
            setRestoreLoading(prev => ({ ...prev, [backupFile]: false }));
        }
    };

    const deleteBackup = async (backupFile) => {
        if (!window.confirm(`Tem certeza que deseja excluir o backup ${backupFile}? Esta ação não pode ser desfeita.`)) {
            return;
        }

        try {
            setDeleteLoading(prev => ({ ...prev, [backupFile]: true }));
            setError(null);
            setSuccess(null);

            await axios.delete(`/api/vm/${vm.nodeId}/${vm.vmid}/backup/${encodeURIComponent(backupFile)}`);
            setSuccess(`Backup ${backupFile} excluído com sucesso!`);

            // Remove from local state
            setBackups(prev => prev.filter(backup => backup.volid !== backupFile));
        } catch (error) {
            console.error('Error deleting backup:', error);
            setError(error.response?.data?.message || 'Erro ao excluir backup');
        } finally {
            setDeleteLoading(prev => ({ ...prev, [backupFile]: false }));
        }
    };

    // Statistics functions
    const loadStatistics = async () => {
        try {
            setStatisticsLoading(true);
            setError(null);

            const response = await axios.get(`/api/vm/${vm.nodeId}/${vm.vmid}/statistics?timerange=${statisticsTimeRange}`);
            setStatistics(response.data.statistics || []);
        } catch (error) {
            console.error('Error loading statistics:', error);
            setError(error.response?.data?.message || 'Erro ao carregar estatísticas');
        } finally {
            setStatisticsLoading(false);
        }
    };

    // VM Notes functions
    const loadVmNotes = async () => {
        try {
            setNotesLoading(true);
            setError(null);

            const response = await axios.get(`/api/vm/${vm.nodeId}/${vm.vmid}/notes`);
            setVmNotes(response.data.notes || '');
        } catch (error) {
            console.error('Error loading VM notes:', error);
            setError(error.response?.data?.message || 'Erro ao carregar observações');
        } finally {
            setNotesLoading(false);
        }
    };

    const saveVmNotes = async (notes) => {
        try {
            setNotesSaving(true);
            setError(null);

            await axios.post(`/api/vm/${vm.nodeId}/${vm.vmid}/notes`, { notes });
            // Removido: notificação de salvamento
        } catch (error) {
            console.error('Error saving VM notes:', error);
            setError(error.response?.data?.message || 'Erro ao salvar observações');
        } finally {
            setNotesSaving(false);
        }
    };

    // Auto-save notes with debounce
    const handleNotesChange = (e) => {
        const newNotes = e.target.value;
        if (newNotes.length <= 1000) {
            setVmNotes(newNotes);

            // Debounce auto-save
            clearTimeout(window.notesTimeout);
            window.notesTimeout = setTimeout(() => {
                saveVmNotes(newNotes);
            }, 1000); // Save after 1 second of inactivity
        }
    };

    const getTimeRangeOptions = () => [
        { value: 'hour', label: '1 hora' },
        { value: 'day', label: '2 dias' },
        { value: 'week', label: '1 semana' },
        { value: 'month', label: '1 mês' }
    ];

    const formatStatisticsData = (data, metric) => {
        if (!data || data.length === 0) return [];

        return data.map(point => {
            // Formatação do tempo baseada na escala selecionada
            let timeFormat;
            const date = new Date(point.time * 1000);

            switch (statisticsTimeRange) {
                case 'hour':
                    timeFormat = date.toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                    break;
                case 'day':
                    timeFormat = date.toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit'
                    });
                    break;
                case 'week':
                    timeFormat = date.toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit'
                    });
                    break;
                case 'month':
                    timeFormat = date.toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit'
                    });
                    break;
                default:
                    timeFormat = date.toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit'
                    });
            }

            return {
                time: timeFormat,
                value: metric === 'cpu' ? (point.cpu * 100).toFixed(1) :
                    metric === 'memory' ? ((point.mem / point.maxmem) * 100).toFixed(1) :
                        metric === 'netin' ? (point.netin / (1024 * 1024)).toFixed(2) :
                            metric === 'netout' ? (point.netout / (1024 * 1024)).toFixed(2) :
                                point[metric],
                rawValue: point[metric]
            };
        });
    };

    // Chart.js configuration functions
    const createChartOptions = (title, yAxisLabel) => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                title: {
                    display: false,
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: yAxisLabel
                    },
                    grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                    }
                },
                x: {
                    grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                    }
                }
            },
            elements: {
                point: {
                    radius: 2,
                    hoverRadius: 4
                }
            }
        };
    };

    const createChartData = (data, label, color) => {
        return {
            labels: data.map(point => point.time),
            datasets: [
                {
                    label: label,
                    data: data.map(point => parseFloat(point.value)),
                    borderColor: color,
                    backgroundColor: color + '20', // Add transparency
                    fill: true,
                    tension: 0.3,
                    borderWidth: 2
                }
            ]
        };
    };

    // Função auxiliar para calcular valores do resumo de forma segura
    const calculateSafeSummary = (data, metric) => {
        if (!data || data.length === 0) return '0';

        const validValues = data
            .map(point => {
                switch (metric) {
                    case 'cpu':
                        return point.cpu && !isNaN(point.cpu) ? point.cpu * 100 : null;
                    case 'memory':
                        return point.mem && point.maxmem && !isNaN(point.mem) && !isNaN(point.maxmem) && point.maxmem > 0
                            ? (point.mem / point.maxmem) * 100 : null;
                    case 'netin':
                        return point.netin && !isNaN(point.netin) ? point.netin / (1024 * 1024) : null;
                    case 'netout':
                        return point.netout && !isNaN(point.netout) ? point.netout / (1024 * 1024) : null;
                    default:
                        return null;
                }
            })
            .filter(value => value !== null && !isNaN(value));

        if (validValues.length === 0) return '0';

        const maxValue = Math.max(...validValues);
        return isNaN(maxValue) ? '0' : maxValue.toFixed(metric === 'netin' || metric === 'netout' ? 2 : 1);
    };

    // Snapshot functions
    const loadSnapshots = async () => {
        try {
            setSnapshotsLoading(true);
            setError(null);

            const response = await axios.get(`/api/vm/${vm.nodeId}/${vm.vmid}/snapshots`);
            setSnapshots(response.data.snapshots || []);
        } catch (error) {
            console.error('Error loading snapshots:', error);
            setError(error.response?.data?.message || 'Erro ao carregar snapshots');
        } finally {
            setSnapshotsLoading(false);
        }
    };

    const createSnapshot = async () => {
        if (snapshotName.trim().length < 2) {
            alert('Nome do snapshot é muito curto!');
            // setError();
            return;
        }

        try {
            setSnapshotLoading(true);
            setError(null);
            setSuccess(null);

            const response = await axios.post(`/api/vm/${vm.nodeId}/${vm.vmid}/snapshot`, {
                snapname: snapshotName.trim(),
                description: snapshotDescription.trim() || undefined
            });

            setSuccess(`Snapshot "${snapshotName}" criado com sucesso!`);
            setSnapshotName('');
            setSnapshotDescription('');
            setShowCreateSnapshotModal(false);

            // Reload snapshots
            setTimeout(() => {
                loadSnapshots();
            }, 2000);
        } catch (error) {
            console.error('Error creating snapshot:', error);
            setError(error.response?.data?.message || 'Erro ao criar snapshot');
        } finally {
            setSnapshotLoading(false);
        }
    };

    const restoreSnapshot = async (snapname) => {
        if (!window.confirm(`Tem certeza que deseja restaurar o snapshot "${snapname}"? A VM será reiniciada e o estado atual será perdido.`)) {
            return;
        }

        try {
            setSnapshotRestoreLoading(prev => ({ ...prev, [snapname]: true }));
            setError(null);
            setSuccess(null);

            await axios.post(`/api/vm/${vm.nodeId}/${vm.vmid}/snapshot/${snapname}/rollback`);
            setSuccess(`Snapshot "${snapname}" restaurado com sucesso! A VM será reiniciada.`);
        } catch (error) {
            console.error('Error restoring snapshot:', error);
            setError(error.response?.data?.message || 'Erro ao restaurar snapshot');
        } finally {
            setSnapshotRestoreLoading(prev => ({ ...prev, [snapname]: false }));
        }
    };

    const deleteSnapshot = async (snapname) => {
        if (!window.confirm(`Tem certeza que deseja excluir o snapshot "${snapname}"? Esta ação não pode ser desfeita.`)) {
            return;
        }

        try {
            setSnapshotDeleteLoading(prev => ({ ...prev, [snapname]: true }));
            setError(null);
            setSuccess(null);

            await axios.delete(`/api/vm/${vm.nodeId}/${vm.vmid}/snapshot/${snapname}`);
            setSuccess(`Snapshot "${snapname}" excluído com sucesso!`);

            // Remove from local state
            setSnapshots(prev => prev.filter(snapshot => snapshot.name !== snapname));
        } catch (error) {
            console.error('Error deleting snapshot:', error);
            setError(error.response?.data?.message || 'Erro ao excluir snapshot');
        } finally {
            setSnapshotDeleteLoading(prev => ({ ...prev, [snapname]: false }));
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                        <Monitor className="h-8 w-8 text-blue-600 mr-3" />
                        {vm.name || `VM ${vm.vmid}`}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                    >
                        ×
                    </button>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-4">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTabModalVM('details')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTabModalVM === 'details'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Detalhes
                        </button>
                        <button
                            onClick={() => setActiveTabModalVM('statistics')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTabModalVM === 'statistics'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <Activity className="h-4 w-4 inline mr-1" />
                            Estatísticas
                        </button>

                        <button
                            onClick={() => setActiveTabModalVM('backups')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTabModalVM === 'backups'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <Archive className="h-4 w-4 inline mr-1" />
                            Backups
                        </button>
                        <button
                            onClick={() => setActiveTabModalVM('snapshots')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTabModalVM === 'snapshots'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <Camera className="h-4 w-4 inline mr-1" />
                            Snapshots
                        </button>
                    </nav>
                </div>

                {/* Alerts */}
                {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {success}
                    </div>
                )}

                {activeTabModalVM === 'details' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Recursos */}
                        <div className="bg-gray-50 col-span-2 p-6 rounded-lg">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <Server className="h-5 w-5 mr-2" />
                                    Recursos
                                </h4>
                                <button
                                    onClick={() => setShowChangeResourcesModal(true)}
                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <Settings className="h-4 w-4 mr-2" />
                                    Alterar recursos da VM
                                </button>
                            </div>
                            <div className="space-y-4">
                                {/* CPU */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-medium text-gray-600 flex items-center">
                                            <Cpu className="h-4 w-4 mr-1" />
                                            CPU
                                        </span>
                                        <span className="text-sm text-gray-900">
                                            {vm.cpu ? `${(vm.cpu * 100).toFixed(1)}%` : '0.0%'} de {vm.maxcpu || 'N/A'} cores
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-600 h-2 rounded-full"
                                            style={{ width: `${vm.cpu ? (vm.cpu * 100) : 0}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* RAM */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-medium text-gray-600 flex items-center">
                                            <MemoryStick className="h-4 w-4 mr-1" />
                                            RAM
                                        </span>
                                        <span className="text-sm text-gray-900">
                                            {formatBytes(vm.mem)} / {formatBytes(vm.maxmem)}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-green-600 h-2 rounded-full"
                                            style={{ width: `${vm.mem && vm.maxmem ? (vm.mem / vm.maxmem * 100) : 0}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Disco */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-medium text-gray-600 flex items-center">
                                            <HardDrive className="h-4 w-4 mr-1" />
                                            Disco
                                        </span>
                                        <span className="text-sm text-gray-900">
                                            {formatBytes(vm.disk)} / {formatBytes(vm.maxdisk)}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-yellow-600 h-2 rounded-full"
                                            style={{ width: `${vm.disk && vm.maxdisk ? (vm.disk / vm.maxdisk * 100) : 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status e Informações Básicas */}
                        <div className="sm:col-span-1 col-span-2 p-6 bg-gray-50 rounded-lg">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <Activity className="h-5 w-5 mr-2" />
                                Status e Informações
                            </h4>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-600">Status:</span>
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(vm.status)}`}>
                                        {getStatusText(vm.status, vm['lock'])}
                                    </span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-sm font-medium text-gray-600">Região:</span>
                                    <span className="text-sm text-gray-900">{vm.clusterName || vm.nodeName}</span>
                                </div>
                                {/* <div className="flex justify-between">
                                    <span className="text-sm font-medium text-gray-600">Servidor:</span>
                                    <span className="text-sm text-gray-900">{vm.realNodeName || vm.node}</span>
                                </div> */}
                                {vm.uptime ? (
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium text-gray-600">Uptime:</span>
                                        <span className="text-sm text-gray-900">{formatUptime(vm.uptime)}</span>
                                    </div>
                                ) : ''}
                                {vm.pid ? (
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium text-gray-600">PID:</span>
                                        <span className="text-sm text-gray-900">{vm.pid}</span>
                                    </div>
                                ) : ''}

                                {/* Observações */}
                                <div className="pt-3 border-t border-gray-200">
                                    <label className="block text-sm font-medium text-gray-600 mb-2">
                                        Observações:
                                    </label>
                                    <textarea
                                        value={vmNotes}
                                        onChange={handleNotesChange}
                                        disabled={notesLoading}
                                        className="w-full h-20 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none disabled:bg-gray-100"
                                        placeholder="Suas observações pessoais..."
                                        maxLength={1000}
                                    />
                                    <div className="flex justify-end mt-1">
                                        <p className="text-xs text-gray-400">
                                            {vmNotes.length}/1000
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Ações */}
                        <div className="sm:col-span-1 col-span-2 p-6 bg-gray-50 rounded-lg">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <Activity className="h-5 w-5 mr-2" />
                                Ações da VM
                            </h4>
                            <div className="space-y-3">
                                
                                {vm.lock?'':(vm.status === 'stopped' ? (
                                    <button
                                        onClick={() => onVMAction(vm.nodeId, vm.type, vm.vmid, 'start')}
                                        disabled={actionLoading[`${vm.nodeId}-${vm.vmid}-start`]}
                                        className="w-full inline-flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                    >
                                        {actionLoading[`${vm.nodeId}-${vm.vmid}-start`] ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                        ) : (
                                            <Play className="h-5 w-5 mr-2" />
                                        )}
                                        {actionLoading[`${vm.nodeId}-${vm.vmid}-start`] ? 'Ligando...' : 'Ligar VM'}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            onClick={() => onVMAction(vm.nodeId, vm.type, vm.vmid, 'stop')}
                                            disabled={actionLoading[`${vm.nodeId}-${vm.vmid}-stop`]}
                                            className="w-full inline-flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                        >
                                            {actionLoading[`${vm.nodeId}-${vm.vmid}-stop`] ? (
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            ) : (
                                                <Square className="h-5 w-5 mr-2" />
                                            )}
                                            {actionLoading[`${vm.nodeId}-${vm.vmid}-stop`] ? 'Desligando...' : 'Desligar VM'}
                                        </button>
                                        <button
                                            onClick={() => onVMAction(vm.nodeId, vm.type, vm.vmid, 'reboot')}
                                            disabled={actionLoading[`${vm.nodeId}-${vm.vmid}-reboot`]}
                                            className="w-full inline-flex justify-center items-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            {actionLoading[`${vm.nodeId}-${vm.vmid}-reboot`] ? (
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600 mr-2"></div>
                                            ) : (
                                                <RotateCcw className="h-5 w-5 mr-2" />
                                            )}
                                            {actionLoading[`${vm.nodeId}-${vm.vmid}-reboot`] ? 'Reiniciando...' : 'Reiniciar VM'}
                                        </button>
                                        <button
                                            onClick={() => onVMAction(vm.nodeId, vm.type, vm.vmid, 'stop', true)}
                                            disabled={actionLoading[`${vm.nodeId}-${vm.vmid}-stop`]}
                                            className="w-full inline-flex justify-center items-center px-4 py-3 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                                        >
                                            {actionLoading[`${vm.nodeId}-${vm.vmid}-stop`] ? (
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600 mr-2"></div>
                                            ) : (
                                                <AlertCircle className="h-5 w-5 mr-2" />
                                            )}
                                            Forçar Desligamento
                                        </button>
                                    </>
                                ))}

                                {/* Console Button */}
                                <button
                                    onClick={() => onConsoleAccess(vm)}
                                    className="w-full inline-flex justify-center items-center px-4 py-3 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 mt-3"
                                >
                                    <Terminal className="h-5 w-5 mr-2" />
                                    Abrir Console
                                </button>
                                <div />
                            </div>
                        </div>


                    </div>
                )}

                {activeTabModalVM === 'backups' && (
                    <div className="space-y-4">
                        {/* Backup Controls */}
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold">Gerenciar Backups</h3>
                            <div className="flex space-x-2">
                                <button
                                    onClick={loadBackups}
                                    disabled={backupsLoading}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <RefreshCw className={`h-4 w-4 mr-2 ${backupsLoading ? 'animate-spin' : ''}`} />
                                    Atualizar
                                </button>
                                <button
                                    onClick={createBackup}
                                    disabled={backupLoading}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {backupLoading ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    ) : (
                                        <Plus className="h-4 w-4 mr-2" />
                                    )}
                                    Criar Backup
                                </button>
                            </div>
                        </div>

                        {/* Backups List */}
                        {backupsLoading ? (
                            <div className="flex justify-center items-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            </div>
                        ) : backups.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <Archive className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum backup encontrado</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Clique em "Criar Backup" para fazer o primeiro backup desta VM.
                                </p>
                            </div>
                        ) : (
                            <div className="bg-white border rounded-lg overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Arquivo
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Data
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Tamanho
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Ações
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {backups.map((backup, index) => (
                                                <tr key={backup.volid} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        backup{backup.volid.includes('.') ? `${backup.volid}`.slice(`${backup.volid}`.indexOf('.')) : ''}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        <div className="flex items-center">
                                                            <Calendar className="h-4 w-4 mr-2" />
                                                            {formatBackupDate(backup.ctime)}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {formatBytes(backup.size)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => restoreBackup(backup.volid)}
                                                                disabled={restoreLoading[backup.volid]}
                                                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                                            >
                                                                {restoreLoading[backup.volid] ? (
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                                ) : (
                                                                    <Download className="h-3 w-3 mr-1" />
                                                                )}
                                                                Restaurar
                                                            </button>
                                                            <button
                                                                onClick={() => deleteBackup(backup.volid)}
                                                                disabled={deleteLoading[backup.volid]}
                                                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                                            >
                                                                {deleteLoading[backup.volid] ? (
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                                ) : (
                                                                    <Trash2 className="h-3 w-3 mr-1" />
                                                                )}
                                                                Excluir
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTabModalVM === 'snapshots' && (
                    <div className="space-y-4">
                        {/* Snapshot Controls */}
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-semibold">Gerenciar Snapshots</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {snapshots.length}/3 snapshots utilizados
                                    {snapshots.length >= 3 && (
                                        <span className="text-red-600 ml-2">• Limite atingido</span>
                                    )}
                                </p>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={loadSnapshots}
                                    disabled={snapshotsLoading}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <RefreshCw className={`h-4 w-4 mr-2 ${snapshotsLoading ? 'animate-spin' : ''}`} />
                                    Atualizar
                                </button>
                                <button
                                    onClick={() => setShowCreateSnapshotModal(true)}
                                    disabled={snapshotLoading || snapshots.length >= 3}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                    title={snapshots.length >= 3 ? 'Limite de 3 snapshots atingido. Exclua um snapshot existente para criar um novo.' : ''}
                                >
                                    <Camera className="h-4 w-4 mr-2" />
                                    Criar Snapshot
                                </button>
                            </div>
                        </div>

                        {/* Limit Warning */}
                        {snapshots.length >= 3 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex">
                                    <AlertCircle className="h-5 w-5 text-yellow-400" />
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-yellow-800">
                                            Limite de snapshots atingido
                                        </h3>
                                        <p className="mt-1 text-sm text-yellow-700">
                                            Esta VM já possui o máximo de 3 snapshots permitidos. Para criar um novo snapshot,
                                            você deve primeiro excluir um dos snapshots existentes.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Snapshots List */}
                        {snapshotsLoading ? (
                            <div className="flex justify-center items-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            </div>
                        ) : snapshots.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <Camera className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum snapshot encontrado</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Clique em "Criar Snapshot" para fazer o primeiro snapshot desta VM.
                                </p>
                            </div>
                        ) : (
                            <div className="bg-white border rounded-lg overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Nome
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Descrição
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Data
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Ações
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {snapshots.map((snapshot, index) => (
                                                <tr key={snapshot.name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {snapshot.name}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {snapshot.description || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        <div className="flex items-center">
                                                            <Calendar className="h-4 w-4 mr-2" />
                                                            {snapshot.snaptime ? new Date(snapshot.snaptime * 1000).toLocaleString('pt-BR') : '-'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => restoreSnapshot(snapshot.name)}
                                                                disabled={snapshotRestoreLoading[snapshot.name]}
                                                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                                            >
                                                                {snapshotRestoreLoading[snapshot.name] ? (
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                                ) : (
                                                                    <RotateCcw className="h-3 w-3 mr-1" />
                                                                )}
                                                                Restaurar
                                                            </button>
                                                            <button
                                                                onClick={() => deleteSnapshot(snapshot.name)}
                                                                disabled={snapshotDeleteLoading[snapshot.name]}
                                                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                                            >
                                                                {snapshotDeleteLoading[snapshot.name] ? (
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                                ) : (
                                                                    <Trash2 className="h-3 w-3 mr-1" />
                                                                )}
                                                                Excluir
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTabModalVM === 'statistics' && (
                    <div className="space-y-6">
                        {/* Statistics Controls */}
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold">Estatísticas da VM</h3>
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                    <label className="text-sm font-medium text-gray-700">Escala:</label>
                                    <select
                                        value={statisticsTimeRange}
                                        onChange={(e) => setStatisticsTimeRange(e.target.value)}
                                        className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {getTimeRangeOptions().map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    onClick={loadStatistics}
                                    disabled={statisticsLoading}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <RefreshCw className={`h-4 w-4 mr-2 ${statisticsLoading ? 'animate-spin' : ''}`} />
                                    Atualizar
                                </button>
                            </div>
                        </div>

                        {/* Statistics Content */}
                        {statisticsLoading ? (
                            <div className="flex justify-center items-center h-64">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            </div>
                        ) : statistics.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <Activity className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma estatística disponível</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Não há dados de estatísticas para o período selecionado.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* CPU Usage Chart */}
                                <div className="bg-white border rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-md font-semibold text-gray-900 flex items-center">
                                            <Cpu className="h-5 w-5 text-blue-500 mr-2" />
                                            Uso de CPU
                                        </h4>
                                        <span className="text-sm text-gray-500">
                                            {statistics.length > 0 && `${(statistics[statistics.length - 1].cpu * 100).toFixed(1)}%`}
                                        </span>
                                    </div>
                                    <div className="h-48">
                                        <Line
                                            options={createChartOptions('', 'Uso %')}
                                            data={createChartData(formatStatisticsData(statistics, 'cpu'), 'CPU', '#3b82f6')}
                                        />
                                    </div>
                                </div>

                                {/* Memory Usage Chart */}
                                <div className="bg-white border rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-md font-semibold text-gray-900 flex items-center">
                                            <MemoryStick className="h-5 w-5 text-green-500 mr-2" />
                                            Uso de Memória
                                        </h4>
                                        <span className="text-sm text-gray-500">
                                            {statistics.length > 0 && `${((statistics[statistics.length - 1].mem / statistics[statistics.length - 1].maxmem) * 100).toFixed(1)}%`}
                                        </span>
                                    </div>
                                    <div className="h-48">
                                        <Line
                                            options={createChartOptions('', 'Uso %')}
                                            data={createChartData(formatStatisticsData(statistics, 'memory'), 'Memória', '#10b981')}
                                        />
                                    </div>
                                </div>

                                {/* Network In Chart */}
                                <div className="bg-white border rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-md font-semibold text-gray-900 flex items-center">
                                            <Network className="h-5 w-5 text-purple-500 mr-2" />
                                            Rede - Entrada
                                        </h4>
                                        <span className="text-sm text-gray-500">
                                            {statistics.length > 0 && `${(statistics[statistics.length - 1].netin / (1024 * 1024)).toFixed(2)} MB/s`}
                                        </span>
                                    </div>
                                    <div className="h-48">
                                        <Line
                                            options={createChartOptions('', 'MB/s')}
                                            data={createChartData(formatStatisticsData(statistics, 'netin'), 'Entrada', '#8b5cf6')}
                                        />
                                    </div>
                                </div>

                                {/* Network Out Chart */}
                                <div className="bg-white border rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-md font-semibold text-gray-900 flex items-center">
                                            <Network className="h-5 w-5 text-orange-500 mr-2" />
                                            Rede - Saída
                                        </h4>
                                        <span className="text-sm text-gray-500">
                                            {statistics.length > 0 && `${(statistics[statistics.length - 1].netout / (1024 * 1024)).toFixed(2)} MB/s`}
                                        </span>
                                    </div>
                                    <div className="h-48">
                                        <Line
                                            options={createChartOptions('', 'MB/s')}
                                            data={createChartData(formatStatisticsData(statistics, 'netout'), 'Saída', '#f59e0b')}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Statistics Summary */}
                        {statistics.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="text-md font-semibold text-gray-900 mb-3">Resumo do Período</h4>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">
                                            {calculateSafeSummary(statistics, 'cpu')}%
                                        </div>
                                        <div className="text-sm text-gray-600">CPU Máximo</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">
                                            {calculateSafeSummary(statistics, 'memory')}%
                                        </div>
                                        <div className="text-sm text-gray-600">RAM Máxima</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-purple-600">
                                            {calculateSafeSummary(statistics, 'netin')} MB/s
                                        </div>
                                        <div className="text-sm text-gray-600">Rede In Máx</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-orange-600">
                                            {calculateSafeSummary(statistics, 'netout')} MB/s
                                        </div>
                                        <div className="text-sm text-gray-600">Rede Out Máx</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}



                {/* Create Snapshot Modal */}
                {showCreateSnapshotModal && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                            <div className="mt-3">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Criar Snapshot
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Nome do Snapshot *
                                        </label>
                                        <input
                                            type="text"
                                            value={snapshotName}
                                            onChange={(e) => setSnapshotName(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Ex: antes-atualizacao"
                                            maxLength={40}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Apenas letras, números, hífen e underscore
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Descrição (opcional)
                                        </label>
                                        <textarea
                                            value={snapshotDescription}
                                            onChange={(e) => setSnapshotDescription(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Descreva o propósito deste snapshot..."
                                            rows={3}
                                            maxLength={255}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end space-x-3 mt-6">
                                    <button
                                        onClick={() => {
                                            setShowCreateSnapshotModal(false);
                                            setSnapshotName('');
                                            setSnapshotDescription('');
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={createSnapshot}
                                        disabled={snapshotLoading || !snapshotName.trim()}
                                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {snapshotLoading ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                                        ) : null}
                                        Criar Snapshot
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Botão Fechar */}
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Fechar
                    </button>
                </div>
            </div>

            {/* Change Resources Modal */}
            {showChangeResourcesModal && (
                <ChangeResourcesModal
                    vm={vm}
                    onClose={() => setShowChangeResourcesModal(false)}
                    onSuccess={() => {
                        setShowChangeResourcesModal(false);
                        setSuccess('Solicitação de alteração de recursos enviada com sucesso!');
                    }}
                />
            )}

        </div>
    );
}

// Change Resources Modal Component
function ChangeResourcesModal({ vm, onClose, onSuccess }) {
    // Definir tamanhos mínimos dos discos baseados na configuração atual
    const currentDiskSize = vm.maxdisk ? Math.round(vm.maxdisk / 1024 / 1024 / 1024) : 20;
    const minDiskSizes = [currentDiskSize]; // Array para suportar múltiplos discos no futuro

    const [formData, setFormData] = useState({
        cpu_cores: vm.maxcpu || 1,
        memory_gb: vm.maxmem ? Math.round(vm.maxmem / 1024 / 1024 / 1024) : 1,
        disks: [{ size_gb: currentDiskSize }],
        region_node_id: vm.nodeId || ''
    });
    const [loading, setLoading] = useState(false);
    const [regions, setRegions] = useState([]);

    useEffect(() => {
        fetchRegions();
    }, []);

    const fetchRegions = async () => {
        try {
            const response = await axios.get('/api/vm-request-config/clusters');
            setRegions(response.data);
        } catch (error) {
            console.error('Erro ao carregar regiões:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await axios.post('/api/vm-change-resources', {
                vmid: vm.vmid,
                nodeId: vm.nodeId,
                currentResources: {
                    cpu_cores: vm.maxcpu || 'N/A',
                    memory_gb: vm.maxmem ? Math.round(vm.maxmem / 1024 / 1024 / 1024) : 'N/A',
                    disks: [{ size_gb: vm.maxdisk ? Math.round(vm.maxdisk / 1024 / 1024 / 1024) : 'N/A' }],
                    region: vm.clusterName || vm.nodeName || 'N/A'
                },
                requestedResources: formData
            });
            onSuccess();
        } catch (error) {
            console.error('Erro ao solicitar alteração de recursos:', error);
            alert(`Erro ao solicitar alteração: ${error.response?.data?.error || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSliderChange = (name, value) => {
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const addDisk = () => {
        setFormData({
            ...formData,
            disks: [...formData.disks, { size_gb: 20 }]
        });
        // Adicionar o tamanho mínimo para o novo disco (novos discos podem começar com 20 GB)
        minDiskSizes.push(20);
    };

    const removeDisk = (index) => {
        if (formData.disks.length > 1) {
            const newDisks = formData.disks.filter((_, i) => i !== index);
            // Remover também o tamanho mínimo correspondente
            minDiskSizes.splice(index, 1);
            setFormData({
                ...formData,
                disks: newDisks
            });
        }
    };

    const handleDiskChange = (index, value) => {
        const newDisks = [...formData.disks];
        // Garantir que o valor não seja menor que o mínimo permitido
        const minSize = index < minDiskSizes.length ? minDiskSizes[index] : 20;
        newDisks[index].size_gb = Math.max(value, minSize);
        setFormData({
            ...formData,
            disks: newDisks
        });
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-md bg-white m-4">
                <div className="mt-3">
                    <h3 className="text-xl font-medium text-gray-900 mb-6">
                        Alterar Recursos da VM - {vm.name || `VM ${vm.vmid}`}
                    </h3>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Current vs Requested Resources Comparison */}
                        <div className="bg-blue-50 p-4 rounded-md mb-6">
                            <h4 className="text-lg font-medium text-blue-900 mb-3">Recursos Atuais vs Solicitados</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <h5 className="font-medium text-blue-800 mb-2">Configuração Atual</h5>
                                    <div className="text-sm text-blue-700 space-y-1">
                                        <div>CPU: {vm.maxcpu || 'N/A'} cores</div>
                                        <div>RAM: {vm.maxmem ? Math.round(vm.maxmem / 1024 / 1024 / 1024) : 'N/A'} GB</div>
                                        <div>Discos: {vm.maxdisk ? Math.round(vm.maxdisk / 1024 / 1024 / 1024) : 'N/A'} GB (total)</div>
                                        <div>Região: {vm.clusterName || vm.nodeName || 'N/A'}</div>
                                    </div>
                                </div>
                                <div>
                                    <h5 className="font-medium text-blue-800 mb-2">Nova Configuração</h5>
                                    <div className="text-sm text-blue-700 space-y-1">
                                        <div>CPU: {formData.cpu_cores} cores</div>
                                        <div>RAM: {formData.memory_gb} GB</div>
                                        <div>Discos: {formData.disks.reduce((total, disk) => total + disk.size_gb, 0)} GB ({formData.disks.length} disco{formData.disks.length !== 1 ? 's' : ''})</div>
                                        <div>Região: {regions.find(r => r.id == formData.region_node_id)?.display_name || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CPU and RAM Sliders */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    CPU Cores: {formData.cpu_cores} {formData.cpu_cores === 1 ? 'core' : 'cores'}
                                </label>
                                <input
                                    type="range"
                                    min="1"
                                    max="16"
                                    step="1"
                                    value={formData.cpu_cores}
                                    onChange={(e) => handleSliderChange('cpu_cores', parseInt(e.target.value))}
                                    className="w-full rounded-lg cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>1</span>
                                    <span>16</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Memória RAM: {formData.memory_gb} GB
                                </label>
                                <input
                                    type="range"
                                    min="1"
                                    max="64"
                                    step="1"
                                    value={formData.memory_gb}
                                    onChange={(e) => handleSliderChange('memory_gb', parseInt(e.target.value))}
                                    className="w-full rounded-lg cursor-pointers"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>1 GB</span>
                                    <span>64 GB</span>
                                </div>
                            </div>
                        </div>

                        {/* Disks Management */}
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <label className="block text-sm font-medium text-gray-700">
                                    Discos da VM
                                </label>
                                <button
                                    type="button"
                                    onClick={addDisk}
                                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                                >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Adicionar Disco
                                </button>
                            </div>

                            {formData.disks.map((disk, index) => (
                                <div key={index} className="mb-4 p-3 border border-gray-200 rounded-md">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-medium text-gray-700">
                                            Disco {index + 1}: {disk.size_gb} GB
                                        </span>
                                        {formData.disks.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeDisk(index)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                    <input
                                        type="range"
                                        min={index < minDiskSizes.length ? minDiskSizes[index] : 10}
                                        max="1000"
                                        step="10"
                                        value={disk.size_gb}
                                        onChange={(e) => handleDiskChange(index, parseInt(e.target.value))}
                                        className="w-full rounded-lg cursor-pointer"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>{index < minDiskSizes.length ? minDiskSizes[index] : 10} GB (mín.)</span>
                                        <span>1 TB</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Region Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Região</label>
                            <select
                                name="region_node_id"
                                value={formData.region_node_id}
                                onChange={handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Selecione uma região</option>
                                {regions.map(region => (
                                    <option key={region.id} value={region.id}>
                                        {region.display_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="bg-yellow-50 p-4 rounded-md">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-yellow-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800">
                                        Importante
                                    </h3>
                                    <div className="mt-2 text-sm text-yellow-700">
                                        <p>
                                            Esta solicitação será analisada pela equipe técnica. Alterações de recursos podem resultar em tempo de inatividade da VM e podem afetar o valor da mensalidade.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                            >
                                {loading ? 'Enviando...' : 'Solicitar Alteração'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;